/**
 * Button component - Premium SaaS style
 */

import {ButtonHTMLAttributes, forwardRef} from 'react';
import {cn} from '../../utils/cn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({className, variant = 'primary', size = 'md', ...props}, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-lg font-medium',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:pointer-events-none',
          'transition-all duration-200',
          'transform active:scale-95',
          {
            // Primary variant
            'bg-orange-600 dark:bg-orange-500 text-white hover:bg-orange-700 dark:hover:bg-orange-400 focus:ring-orange-600 dark:focus:ring-orange-500 hover:shadow-lg hover:shadow-orange-600/25 dark:hover:shadow-orange-500/25':
              variant === 'primary',
            
            // Secondary variant
            'bg-gray-100 dark:bg-[#2D2D30] text-gray-900 dark:text-[#CCCCCC] hover:bg-gray-200 dark:hover:bg-[#3D3D40] focus:ring-gray-400 dark:focus:ring-gray-600':
              variant === 'secondary',
            
            // Outline variant
            'border border-gray-300 dark:border-gray-700 bg-transparent text-textPrimary dark:text-[#CCCCCC] hover:bg-gray-50 dark:hover:bg-[#2D2D30] focus:ring-gray-400 dark:focus:ring-gray-600':
              variant === 'outline',
            
            // Ghost variant
            'bg-transparent text-textPrimary dark:text-[#CCCCCC] hover:bg-gray-50 dark:hover:bg-[#2D2D30] focus:ring-gray-400 dark:focus:ring-gray-600':
              variant === 'ghost',
            
            // Danger variant
            'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500':
              variant === 'danger',
            
            // Size variants
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-base': size === 'md',
            'px-6 py-3 text-lg': size === 'lg',
          },
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

